import { useState } from 'react';
import { Button, IconButton, InputAdornment, CircularProgress, Card, TextField, Typography, Grid } from '@mui/material';
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';

import * as S from './styles';
import { CustomerService } from '../../services/customer.service';
import { toast } from 'react-toastify';
import { passwordField } from '../../schemas/password-policy';
import { PasswordRequirements } from '../password-requirements';

export const CustomerChangePasswordForm = () => {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const schema = Yup.object().shape({
        // Current password only needs to be present — existing accounts may have
        // pre-policy (shorter) passwords and must still be able to authenticate
        // here in order to change them.
        currentPassword: Yup
            .string()
            .required('Please enter your password'),
        // Only the confirm field carries the match check. Putting `.oneOf` on
        // newPassword too makes it show "Passwords must match" the moment it's
        // touched — before the user has reached the confirm field.
        newPassword: passwordField('Please enter your new password'),
        confirmNewPassword: Yup
            .string().oneOf(
                [Yup.ref("newPassword"), null],
                "Passwords must match"
            )
            .required('Please confirm your password')
        });

    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            setLoading(true);
            await CustomerService.changePassword(values.currentPassword, values.newPassword, (isValid, msg) => {
                setLoading(false);
                if (!isValid) {
                    toast(
                        msg,
                        {
                            autoClose: 1500,
                            type: 'error',
                        }
                    );
                    return
                }

                toast(
                    'Password successfully changed!',
                    {
                        autoClose: 1500,
                        type: 'success',
                    },
                );

                formik.resetForm();

            });
        }
    });

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const { errors, touched, handleSubmit, getFieldProps } = formik;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} sm={12} lg={6} xl={6} md={6}>
                <Card sx={{ p: 2, justifyContent: 'center', justifyItems: 'center' }}>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            <S.ResetContainer>
                                <Typography variant="h5">Change Password</Typography>
                                <Typography
                                    mt="10px"
                                    mb="32px"
                                    variant="subtitle1"
                                    color="grayMedium.main"
                                >
                                    Choose a new password to access POM Safe.
                                </Typography>

                                
                                <S.FormGroup>
                                    <TextField
                                        label="Current password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowPassword} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.currentPassword && errors.currentPassword)}
                                        helperText={touched.currentPassword && errors.currentPassword}
                                        {...getFieldProps('currentPassword')}
                                    />
                                </S.FormGroup>

                                <S.FormGroup>
                                    <TextField
                                        fullWidth
                                        label="New password"
                                        type={showPassword ? "text" : "password"}
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowPassword} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.newPassword && errors.newPassword)}
                                        helperText={touched.newPassword && errors.newPassword}
                                        {...getFieldProps('newPassword')}
                                    />
                                </S.FormGroup>

                                <S.FormGroup>
                                    <TextField
                                        fullWidth
                                        label="Confirm New password"
                                        type={showPassword ? "text" : "password"}
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleShowPassword} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={Boolean(touched.confirmNewPassword && errors.confirmNewPassword)}
                                        helperText={touched.confirmNewPassword && errors.confirmNewPassword}
                                        {...getFieldProps('confirmNewPassword')}
                                    />
                                </S.FormGroup>

                                <PasswordRequirements password={formik.values.newPassword} />

                                <Button
                                    size="large"
                                    disabled={loading}
                                    variant="contained"
                                    type="submit"
                                    fullWidth
                                >
                                    {loading ? (
                                        <CircularProgress size={27} sx={{ color: '#ffffff' }} />
                                    ) : (
                                        'Change Password'
                                    )}
                                </Button>
                            </S.ResetContainer>
                        </Form>
                    </FormikProvider>
                </Card>
            </Grid>    
        </Grid>
    )
};